
import { mapGetters } from 'vuex'
import { soaCountersignLabels, soaFormAnswers, soaSigningState } from '~/util/soa'
import { address } from '~/util/string'

const allProducts = () => ({ mapd: true, pdp: true, supp: true, dental_vision_hearing: true })

export default {
    props: {
        person: {
            type: Object,
            default: null
        },
        soa: {
            type: Object,
            default: null
        },
        skipSoa: {
            type: Boolean,
            default: false
        },
        soa60days: {
            type: [String, Object],
            default: null
        },
        titleClass: {
            type: String,
            default: 'text-primary'
        },
        titleHidden: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            signed: false,
            resigning: false,
            pdfLoading: true,
            // the Item returned by the sign/replace endpoints
            signedItem: null,
            pdfPollTimer: null,
            pdfPollAttempts: 0,
            form: {
                role: 'beneficiary',
                typed_name: '',
                products: allProducts(),
                agent_name: ''
            },
        }
    },
    computed: {
        ...mapGetters([
            'agent'
        ]),
        signedAgentName () {
            return (this.csoa60days || this.csoa)?.agent_name || this.form.agent_name || ''
        },
        signedSoaDateTime () {
            return this.$moment((this.soa60days || this.soa)?.created).format('MMMM D, YYYY [at] h:mm A')
        },
        csoa60days () {
            return this.soa60days?.value ? this.soa60days.value : this.soa60days
        },
        csoa () {
            return this.soa?.value ? this.soa.value : this.soa
        },
        // the signed SOA on display: /survey/{uid}/soa passes the client's
        // latest as `soa60days`, the single-SOA page as `soa`
        record () {
            return this.csoa || this.csoa60days
        },
        readonly () {
            if (this.resigning) {
                return false
            }
            return !!(this.csoa || this.signed || this.soa60days || this.skipSoa)
        },
        canSubmit () {
            if (this.canAgent) {
                return false
            }
            return this.form.typed_name
        },
        // the Item row (id/key/value) backing the displayed SOA
        soaItem () {
            if (this.signedItem?.id) {
                return this.signedItem
            }
            if (this.soa?.id) {
                return this.soa
            }
            if (this.soa60days?.id) {
                return this.soa60days
            }
            return null
        },
        pdfUrl () {
            const item = this.soaItem
            // only Documenso-signed SOAs have a pdf to embed
            if (!item?.id || !item.value?.envelope_item_id) {
                return null
            }
            const uid = this.canAgent ? (this.person?.id || item.user_id || this.$route.params.uid) : null
            const url = this.$api.signedSoaPdfUrl(uid, item.key || item.id)
            // same-day re-signs keep the same item key; vary the url by the
            // envelope item so the iframe reloads the fresh pdf
            return `${url}?v=${encodeURIComponent(item.value.envelope_item_id)}`
        },
        canResign () {
            return !!(this.soaItem?.id && this.readonly && !this.skipSoa && !this.canAgent)
        },
        // the advisor's two fields as labels, null = not filled yet (util/soa.js)
        countersign () {
            return soaCountersignLabels(this.soaItem || this.soa || this.soa60days, this.$moment)
        },
        // signed by the client, countersigned only when the AEP recommendation
        // goes out (util/soa.js) -- no envelope exists yet, so nothing to poll
        awaitingAgent () {
            return soaSigningState(this.soaItem).awaitingAgent
        },
        // signed through Documenso but the background signing hasn't linked the
        // envelope yet; on signing failure the backend strips `provider`, which
        // turns this off and leaves the pretty-print form as the signed record
        pdfPending () {
            const value = this.soaItem?.value
            return !!(value && value.provider === 'documenso' && !value.envelope_item_id &&
                value.provider_sign_status !== 'error' && !this.awaitingAgent)
        },
        // the pretty-print form is only shown when there is no Documenso pdf
        // to display (legacy SOAs, not yet signed) or while re-signing
        showForm () {
            return this.resigning || !this.pdfUrl
        },
        personFullName () {
            return this.titlelize(this.record?.full_name || (this.person?.first_name + ' ' + this.person?.last_name))
        },
        personAddress () {
            return this.record?.address || this.person?.c_address || address(this.person)
        },
        personPhone () {
            return this.record?.phone || this.person?.phone || this.person?.cell_phone || this.person?.home_phone
        },
        isPdpOnly () {
            return this.form.products.pdp && !this.form.products.mapd && !this.form.products.supp && !this.form.products.dental_vision_hearing
        }
    },
    watch: {
        // The client's own answers, also when the record loads after mount (an
        // agent reading an AEP-survey SOA saw every product ticked, 2026-10-03).
        record: {
            immediate: true,
            handler (record) {
                const answers = soaFormAnswers(record)
                if (answers) {
                    this.form.products = answers.products
                    this.form.role = answers.role
                }
                this.form.typed_name = record?.typed_name || this.personFullName
            }
        },
        pdfUrl (v) {
            if (v) {
                this.pdfLoading = true
            }
        },
        pdfPending: {
            immediate: true,
            handler (pending) {
                if (pending) {
                    this.startPdfPolling()
                } else {
                    this.stopPdfPolling()
                }
            }
        }
    },
    beforeDestroy () {
        this.stopPdfPolling()
    },
    methods: {
        startResign () {
            this.resigning = true
            this.signed = false
            // a new SOA starts with every product ticked (legal, 2026-09-24),
            // not the answers on the one it replaces
            this.form.products = allProducts()
        },
        // while the background signing runs, refetch the SOA until the
        // envelope ids appear (pdf ready) or `provider` is stripped (failed,
        // the pretty-print form stays as the signed record)
        startPdfPolling () {
            if (this.pdfPollTimer) {
                return
            }
            this.pdfPollAttempts = 0
            this.pdfPollTimer = setInterval(async () => {
                if (!this.pdfPending || ++this.pdfPollAttempts > 45) {
                    this.stopPdfPolling()
                    return
                }
                const item = this.soaItem
                const uid = this.canAgent ? (this.person?.id || item.user_id || this.$route.params.uid) : null
                try {
                    const resp = await this.$api.getSignedSoa(uid, item.key || item.id)
                    if (resp?.id) {
                        this.signedItem = resp
                    }
                } catch (e) {
                    // transient — keep polling until the attempts run out
                }
            }, 4000)
        },
        stopPdfPolling () {
            if (this.pdfPollTimer) {
                clearInterval(this.pdfPollTimer)
                this.pdfPollTimer = null
            }
        },
        async submitSoa () {
            // agents signatures are autosigned
            if (!this.canSubmit) {
                this.$alert({ message: 'Please enter your full name and agree to the electronic signature statement.', variant: 'warning' })
                return
            }

            const payload = {
                full_name: this.personFullName,
                address: this.personAddress,
                phone: this.personPhone,
                client_id: this.person?.id,
                role: this.form.role,
                typed_name: this.form.typed_name,
                products: this.form.products,
            }

            this.$emit('signature', payload)
            this.signed = true

            if (!payload.client_id) {
                return
            }

            this.$store.dispatch('busy')

            // re-signing replaces the previous SOA (deletes it from Documenso too)
            const replacedId = this.resigning ? this.soaItem?.id : null
            const resp = replacedId
                ? await this.$try('replaceSignedSoa', replacedId, payload)
                : await this.$try('createSignedSoaDocumenso', payload)
            if (resp.error) {
                this.signed = false
            }

            this.$store.dispatch('idle')
            if (!resp || resp.error) {
                this.$alert({ message: (resp && resp.error) ? resp.error : 'Failed to submit SOA. Please try again.', variant: 'danger' })
                return
            }
            this.signedItem = resp
            this.resigning = false
            if (resp.agent_name || resp.value?.agent_name) {
                this.form.agent_name = resp.agent_name || resp.value?.agent_name
            }
            this.$alert({ message: 'Signed SOA uploaded successfully', variant: 'success' })
            this.$emit('soa60daysUpdate', resp)
        }
    }
}
